import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import EnterpriseLayout from '../components/layout/EnterpriseLayout';
import { CreditSummaryResponse } from '../utils/creditService';
import { getAllCreditSummaries } from '../utils/creditService';
import TimezoneAwareTimestamp from '../components/common/TimezoneAwareTimestamp';
import styles from '../styles/views/CreditLines.module.scss';

interface CreditLineCardProps {
  creditLine: CreditSummaryResponse & { id: number };
  onClick: () => void;
}

const CreditLineCard: React.FC<CreditLineCardProps> = ({ creditLine, onClick }) => {
  const utilizationPercent = (creditLine.available_balances.used / creditLine.available_balances.balance) * 100;
  const hasOverdueFees = creditLine.credit_fees_owed > 0 && creditLine.days_overdue > 0;

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getMonthsRemaining = () => {
    const maturityDate = new Date(creditLine.maturity_date);
    const now = new Date();
    const monthsDiff = Math.ceil((maturityDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30));
    return Math.max(0, monthsDiff);
  };

  return (
    <article
      className={clsx(styles.creditLineCard, 'credit-line-card')}
      onClick={onClick}
    >
      {/* Card Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <h3 className={styles.creditLineName}>{creditLine.label}</h3>
          <div className={styles.commitmentLabel}>Credit Limit</div>  
          <div className={styles.primaryAmount}>{formatAmount(creditLine.maximum_commitment)}</div>
          <div className={styles.cardMeta}>
            <span className={styles.metaInfo}>{creditLine.available_balances.currency} • {creditLine.network}</span>
            <div className={`${styles.statusBadge} ${styles[creditLine.status.toLowerCase()]}`}>
              <div className={styles.statusDot}></div>
              <span>{creditLine.status.toUpperCase()}</span>
            </div>
          </div>
        </div>
        <div className={styles.expiryInfo}>
          <span className={styles.expiryLabel}>Expires</span>
          <TimezoneAwareTimestamp 
            dateString={creditLine.maturity_date} 
            format="full"
            className={styles.expiryDate}
          />
          <span className={styles.monthsLeft}>{getMonthsRemaining()} months left</span>
        </div>
      </div>

      {/* Balance Overview */}
      <div className={styles.balanceOverview}>
        <div className={styles.balanceItem}>
          <div className={styles.balanceLabel}>Available Balance</div>
          <div className={styles.balanceAmount}>{formatAmount(creditLine.available_balances.available_credit_to_request)}</div>
        </div>
        <div className={styles.balanceDivider}></div>
        <div className={styles.balanceItem}>
          <div className={styles.balanceLabel}>Credit Outstanding</div>
          <div className={styles.balanceAmount}>{formatAmount(creditLine.available_balances.credit_balance - creditLine.available_balances.pending_repayments)}</div>
        </div>
        {/* Pending Repayments */}
        {creditLine.available_balances.pending_repayments > 0 && (
          <div className={styles.pendingRepayments}>
            <div className={styles.pendingIcon}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="64" strokeDashoffset="0">
                  <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="2s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            <span className={styles.pendingText}>Pending repayments: {formatAmount(creditLine.available_balances.pending_repayments)}</span>
          </div>
        )}        
      </div>

      {/* Usage Summary */}
      <div className={styles.usageSummary}>
        {formatAmount(creditLine.available_balances.credit_balance)} used of {formatAmount(creditLine.maximum_commitment)} total limit
        <div className={styles.progressBar}>
          <div 
            className={clsx(styles.progress, utilizationPercent > 80 && styles.high)} 
            style={{ width: `${utilizationPercent}%` }}
          ></div>
        </div>
      </div>

      {/* Fees Section */}
      <div className={styles.feesSection}>
        <div className={styles.feesHeaderRow}>
          <span className={styles.feesLabel}>Fees</span>
          <span className={clsx(styles.feesStatus, hasOverdueFees && styles.overdue)}>
            {hasOverdueFees ? 'Payment Due' : 'Current'}
          </span>
        </div>
        {creditLine.credit_fees_owed > 0 ? (
          hasOverdueFees ? (
            <div className={clsx(styles.feesAlert, styles.overdue)}>
              <span className={styles.feesAmount}>{formatAmount(creditLine.credit_fees_owed)} owed</span>
              <span className={styles.feesNote}>Payment overdue by {creditLine.days_overdue} days</span>
            </div>
          ) : (
            <div className={styles.feesNeutral}>
              <span className={styles.feesAmount}>{formatAmount(creditLine.credit_fees_owed)} due</span>
              <span className={styles.feesNote}>Based on this month's usage</span>
            </div>
          )
        ) : (
          <div className={styles.feesGood}>
            No outstanding fees
          </div>
        )}
      </div>

      {/* Bottom Stats */}
      <div className={styles.bottomStats}>
        <div className={styles.stat}>
        <div className={styles.statValue}>{(creditLine.recycled_capital / creditLine.maximum_commitment).toFixed(2)} x</div>
        <span className={styles.statLabel}>Recyled Capital</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{getMonthsRemaining()}</span>
          <span className={styles.statLabel}>months remaining</span>
        </div>
      </div>
    </article>
  );
};

const CreditLines: React.FC = () => {
  const navigate = useNavigate();

  const [creditLines, setCreditLines] = useState<(CreditSummaryResponse & { id: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllCreditLines, setShowAllCreditLines] = useState(true);

  useEffect(() => {
    const fetchCreditLines = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAllCreditSummaries();

        // Transform the response to include id from available_balances.id
        const creditLinesWithId = response.map((creditLine) => ({
          ...creditLine,
          id: creditLine.available_balances.id
        }));

        setCreditLines(creditLinesWithId);
        
        // Debug: Log the status values to understand filtering
        console.log('Credit lines with status values:', creditLinesWithId.map(cl => ({ id: cl.id, status: cl.status })));
      } catch (err) {
        console.error('Error fetching credit lines:', err);
        setError('Failed to load credit lines. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCreditLines();
  }, []);

  const handleCreditLineClick = (creditLineId: number) => {
    navigate(`/credit-lines/${creditLineId}`);
  };

  return (
    <EnterpriseLayout 
      pageTitle="Credit Lines"
      pageDescription="Manage your credit facilities and view detailed information about each credit line."
    >
      <div className={clsx(styles.creditLinesPage, 'credit-lines-page')}>
        {/* Filter Controls */}
        <div className={styles.pageControls}>
          <div className={styles.filterToggle}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={showAllCreditLines}
                onChange={(e) => setShowAllCreditLines(e.target.checked)}
                className={styles.toggleInput}
              />
              <span className={styles.toggleSlider}></span>
              <span className={styles.toggleText}>
                {showAllCreditLines ? 'Showing All Credit Lines' : 'Showing Active Only'}
              </span>
            </label>
          </div>
        </div>

        {/* Credit Lines Grid */}
        <div className={styles.creditLinesGrid}>
          {loading && (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="64" strokeDashoffset="0">
                    <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="1s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>
              <p>Loading credit lines...</p>
            </div>
          )}

          {error && (
            <div className={styles.errorState}>
              <div className={styles.errorIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" />
                  <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3>Unable to Load Credit Lines</h3>
              <p>{error}</p>
              <button
                className={styles.retryButton}
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && creditLines
            .filter(creditLine => showAllCreditLines || creditLine.status.toLowerCase() === 'active')
            .map((creditLine) => (
              <CreditLineCard
                key={creditLine.id}
                creditLine={creditLine}
                onClick={() => handleCreditLineClick(creditLine.id)}
              />
            ))
          }

          {!loading && !error && creditLines.filter(creditLine => showAllCreditLines || creditLine.status.toLowerCase() === 'active').length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M2 6c0-1.505.78-3.08 2.324-4.093C5.867 1.393 7.5 1.5 9 2.5L17 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 17v5c0 1.105.895 2 2 2h6c1.105 0 2-.895 2-2v-5" stroke="currentColor" strokeWidth="2" />
                  <rect x="4" y="10" width="16" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3>No Credit Lines Available</h3>
              <p>You don't have any active credit lines yet. Contact your account manager to set up a credit facility.</p>
            </div>
          )}
        </div>
      </div>
    </EnterpriseLayout>
  );
};

export default CreditLines;
